import React, { useState, forwardRef, useImperativeHandle, useRef, useEffect, ReactNode } from "react";
import { Link, useNavigate } from "react-router-dom";
import { 
  ChevronsDown, 
  ChevronsUp, 
  Clock, 
  Flag, 
  ChevronLeft, 
  ChevronRight, 
  X, 
  ImagePlus,
  Ellipsis,
  Pencil,
  Trash,
  Download,
  Loader2,
  Megaphone
} from "lucide-react";
import api from "config/axiosConfig";
import { transformHtmlContent } from "util/transformHtmlContent";
import { useUser } from "util/UserContext";
import { useAnonEntityOwnership } from "hooks/useAnonEntityOwnership";
import { downloadSingleKey } from "util/anonTokenBackup";
import { toast } from "sonner";
import { 
  loadCoauthorData, 
  handleCoauthorSelect as selectCoauthor, 
  handleCoauthorRemove as removeCoauthor 
} from "util/entityUtils";
import { PrivacyLevel } from "util/userUtils";

import { 
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "ui/DropdownMenu";
import { Drawer, DrawerContent, DrawerTrigger } from "ui/Drawer";
import DialogConfirmation from "ui/DialogConfirmation";
import DetailPanel from "ui/DetailPanel";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
  CarouselDots,
  CarouselApi,
} from "ui/Carousel";
import EntitySubscription from "ui/EntitySubscription";

// Entity-specific components
import Report from "components/Entities/Report";
import EntityActions from "components/Entities/EntityActions";
import EntityAvatar from "components/Entities/EntityAvatar";
import EntityPrivacy from "components/Entities/EntityPrivacy";
import TextEditor from "components/TextEditor";
import PromoteEntityModal from "components/Promotions/PromoteEntityModal";
import { getStoryUiStrings, resolveStoryLocale } from "i18n/storyUi";
import CoauthorSelect, { CoauthorSuggestion } from "components/Coauthors/CoauthorSelect";
import VideoPlayer from "components/Common/VideoPlayer";
import InputTextArea from "ui/InputTextArea";
import { resolveMediaUrl } from "util/mediaUrl";
import type { ExistingMediaFile } from "util/mediaTypes";
import { uploadEntityMediaDirect } from "util/entityMediaUpload";
import { apiRoutes } from "util/apiRoutes";
import PostMediaPreview from "components/Posts/display/PostMediaPreview";
import PostReferenceLink from "components/Posts/display/PostReferenceLink";
import MediaCarousel from "ui/MediaCarousel";
import MediaViewer from "components/Entities/MediaViewer";
import { SurfaceLayerProvider, useResolvedSurfaceLayer, useActiveSurfaceLayer } from "ui/layers";

// Base entity interface
export interface BaseEntity {
  id: string | number;
  user_id: string | number;
  title?: string | null;
  body?: string | null;
  url?: string | null;
  created_at: string;
  properties?: {
    privacy?: PrivacyLevel;
    coauthors?: CoauthorSuggestion[];
    media_files?: ExistingMediaFile[];
    [key: string]: any;
  };
  viewer_relation?: any;
  [key: string]: any;
}

// Re-exported for the existing card-wrapper import sites; canonical home is
// util/mediaTypes (ui/ must not import from components/ — #597).
export type { ExistingMediaFile } from "util/mediaTypes";

export interface EntityCardRef {
  applyMediaOrderChanges: () => Promise<boolean>;
}

export interface EntityCardProps<T extends BaseEntity> {
  entity: T;
  entityType: "impact" | "solution" | "feedback" | "action" | "teabate" | "collection";
  showMeta?: boolean;
  truncateDescription?: boolean;
  isEntityProfilePage?: boolean;
  isEntitySection?: boolean;
  showActions?: boolean;
  editMode?: boolean;
  onDescriptionChange?: (value: string) => void;
  onTitleChange?: (value: string) => void;
  onPrivacyChange?: (value: string) => void;
  onCoauthorsChange?: (addedCoauthors: CoauthorSuggestion[], removedCoauthors: CoauthorSuggestion[]) => void;
  
  // Render props for entity-specific content
  renderTags?: (entity: T) => ReactNode;
  renderEditFields?: (entity: T) => ReactNode;
  renderAdditionalContent?: (entity: T) => ReactNode;
  renderActions?: (entity: T) => ReactNode;
  /**
   * Prominent visual block rendered above the media-display in view mode.
   * Used by graph collections (and similar "primary visualization" posts) to
   * make the interactive canvas the headline content of the card without
   * replacing the usual media/url preview that may live below it.
   */
  renderPrimaryContent?: (entity: T) => ReactNode;
  
  // Entity-specific handlers
  onEdit?: () => void;
  onDelete?: () => void;
  
  // Custom configurations
  hideTitle?: boolean;
  descriptionPlaceholder?: string;
  canEditTitle?: boolean;
  canEditPrivacy?: boolean;
  titleValidationMessage?: string;
  titleMaxLength?: number;
  
  children?: ReactNode;
}

const EntityCard = forwardRef(
  <T extends BaseEntity>({
    entity,
    entityType,
    showMeta = true,
    truncateDescription = true,
    isEntityProfilePage = false,
    isEntitySection = false,
    showActions = true,
    editMode = false,
    onDescriptionChange,
    onTitleChange,
    onPrivacyChange,
    onCoauthorsChange,
    renderTags,
    renderEditFields,
    renderAdditionalContent,
    renderActions,
    renderPrimaryContent,
    onEdit,
    onDelete,
    hideTitle = false,
    descriptionPlaceholder = "Description",
    canEditTitle = false,
    canEditPrivacy = true,
    titleValidationMessage,
    titleMaxLength = 300,
    children,
  }: EntityCardProps<T>, ref: React.Ref<EntityCardRef>) => {
    
    const { currentUser, setShowAuthModal, userIsMobile } = useUser();
    const navigate = useNavigate();
    const TruncateMarkup = require("react-truncate-markup").default;
    const IMAGE_CDN = process.env.REACT_APP_IMAGE_CDN || "";

    // Common states
    const [isExpanded, setIsExpanded] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [isReportModalOpen, setIsReportModalOpen] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isPromoteModalOpen, setIsPromoteModalOpen] = useState(false);
    const [isMediaViewerOpen, setIsMediaViewerOpen] = useState(false);
    const entityCardStrings = getStoryUiStrings(resolveStoryLocale());
    const [isTruncated, setIsTruncated] = useState(false);

    // Provide a layer context for modals opened while MediaViewer is active
    const activeLayer = useActiveSurfaceLayer();
    const mediaViewerLayer = useResolvedSurfaceLayer({ tier: "modal", layerBase: activeLayer?.childSurface });
    const modalLayerProviderValue = React.useMemo(
      () => ({ ...mediaViewerLayer, contentElement: null }),
      [mediaViewerLayer]
    );
    
    // Coauthor states
    const [existingCoauthors, setExistingCoauthors] = useState<CoauthorSuggestion[]>((entity.properties?.coauthors || []) as CoauthorSuggestion[]);
    const [newCoauthors, setNewCoauthors] = useState<CoauthorSuggestion[]>([]);
    const [removedCoauthors, setRemovedCoauthors] = useState<CoauthorSuggestion[]>([]);
    const [pendingCoauthors, setPendingCoauthors] = useState<CoauthorSuggestion[]>([]);
    const [isLoadingCoauthors, setIsLoadingCoauthors] = useState<boolean>(false);
    
    // Media states
    const [isUploadingMedia, setIsUploadingMedia] = useState<boolean>(false);
    const [isDeletingMedia, setIsDeletingMedia] = useState<string | null>(null);
    const [localMediaOrder, setLocalMediaOrder] = useState<{ [key: string]: number }>({});
    const [currentCarouselIndex, setCurrentCarouselIndex] = useState<number>(0);
    const carouselApiRef = useRef<CarouselApi>();
    const [refreshKey, setRefreshKey] = useState<number>(0);
    
    // Privacy state
    const [privacy, setPrivacy] = useState<PrivacyLevel>((entity.properties?.privacy as PrivacyLevel) || "public");
    
    // Check if user has anonymous edit token for this entity
    const hasAnonToken = useAnonEntityOwnership(
      entityType,
      entity.id,
      typeof entity.user_id === 'string' ? parseInt(entity.user_id, 10) : entity.user_id,
      entity.properties?.privacy
    );
    
    // Computed values
    const entityHref = `/${entityType}/${encodeURIComponent(entity.id)}`;
    const shouldHideTitle = hideTitle || !entity.title || entity.title.trim() === "" || entity.title.trim() === "[no_title]";
    
    // Expose the applyMediaOrderChanges function to parent component
    useImperativeHandle(ref, () => ({
      applyMediaOrderChanges
    }));
    
    // Load coauthor data
    useEffect(() => {
      if (entity.id && currentUser?.id && entity.user_id != null && currentUser.id.toString() === entity.user_id.toString()) {
        loadCoauthorData(
          {
            id: Number(entity.id),
            user_id: Number(entity.user_id),
            properties: entity.properties
          },
          entityType,
          Number(currentUser.id),
          {
            setExistingCoauthors,
            setPendingCoauthors,
            setIsLoadingCoauthors
          }
        );
      }
    }, [entity.id, entity.user_id, entity.properties, currentUser?.id, entityType]);
    
    // Clear local media order changes when edit mode is disabled
    useEffect(() => {
      if (!editMode) {
        setLocalMediaOrder({});
        setCurrentCarouselIndex(0);
      }
    }, [editMode]);
    
    // Common handlers
    const handleCardClick = (event: React.MouseEvent<HTMLDivElement>) => {
      const element = event.target as HTMLElement;
      if (element.tagName.toLowerCase() === "a") return;
      if (element.classList.contains("read-more")) return;
      if (element.classList.contains("external-mention")) return;
      if (element.classList.contains("mention")) return;
      if (element.closest('a, .external-mention, .mention')) return;
      if (element.classList.contains("video-player-container")) {
        event.preventDefault();
        return;
      };
      if (!isEntityProfilePage) {
        event.stopPropagation();
        navigate(entityHref);
      } else {
        event.preventDefault();
        if (element.tagName.toLowerCase() === "img" || 
            element.tagName.toLowerCase() === "video" ||
            element.tagName.toLowerCase() === "svg" || 
            element.tagName.toLowerCase() === "path" || 
            element.classList.contains("no-metadata-link")) {
          event.stopPropagation();
          if (entity.url) {
            window.open(entity.url, "_blank", "noopener,noreferrer");
          }
        }
      }
    };
    
    const handleDescriptionClick = (event: React.MouseEvent) => {
      if (!truncateDescription || !isTruncated) return;
      
      // Check if the click originated from a link or interactive element
      const target = event.target as HTMLElement;
      const isLinkClick = target.closest('a, button, .external-mention, .mention, [role="button"]');
      
      // Don't toggle expansion if clicking on a link or interactive element
      if (isLinkClick) {
        event.stopPropagation();
        return;
      }
      
      event.preventDefault();
      if (isEntityProfilePage && truncateDescription) {
        setIsExpanded(!isExpanded);
      }
    };
    
    const handleReadMoreClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      setIsExpanded(!isExpanded);
      event.stopPropagation();
    };
    
    const handleReportClick = () => {
      if (!currentUser) {
        setShowAuthModal(true);
      } else {
        setMenuOpen(false);
        setDrawerOpen(false);
        setIsReportModalOpen(true);
      }
    };
    
    const handleEditClick = () => {
      setMenuOpen(false);
      setDrawerOpen(false);
      if (onEdit) {
        onEdit();
      } else {
        navigate(`/${entityType}/${entity.id}?edit=true`);
      }
    };
    
    const handleDeleteClick = () => {
      setMenuOpen(false);
      setDrawerOpen(false);
      setIsDeleteDialogOpen(true);
    };
    
    const confirmDelete = async () => {
      try {
        if (onDelete) {
          await onDelete();
        } else {
          await api.delete(apiRoutes.posts.byId(entity.id));
        }
        setIsDeleteDialogOpen(false);
        if (isEntityProfilePage) {
          navigate("/");
        } else {
          window.location.reload();
        }
      } catch (error) {
        console.error(`Error deleting ${entityType}:`, error);
      }
    };
    
    const cancelDelete = () => {
      setIsDeleteDialogOpen(false);
    };
    
    const handleDownloadKey = async () => {
      setMenuOpen(false);
      setDrawerOpen(false);
      try {
        await downloadSingleKey(entityType, entity.id);
        toast.success("Edit key downloaded");
      } catch {
        toast.error("Failed to download key");
      }
    };
    
    // Media handlers
    const uploadMediaFiles = async (files: FileList) => {
      try {
        setIsUploadingMedia(true);
        await uploadEntityMediaDirect(entityType, Number(entity.id), Array.from(files).map((file, index) => ({
          file,
          order: index + 1
        })));
        const response = await api.get(apiRoutes.entities.media(entityType, entity.id));
        if (response.data?.mediaFiles) {
          entity.properties = entity.properties || {};
          entity.properties.media_files = response.data.mediaFiles;
          setRefreshKey(prev => prev + 1);
        }
      } catch (error) {
        console.error("Error uploading media files:", error);
      } finally {
        setIsUploadingMedia(false);
      }
    };
    
    const deleteMediaFile = async (mediaKey: string) => {
      try {
        setIsDeletingMedia(mediaKey);
        
        const encodedMediaKey = encodeURIComponent(mediaKey);
        const response = await api.delete(apiRoutes.entities.mediaKey(entityType, entity.id, encodedMediaKey));
        
        if (response.data?.entity) {
          entity.properties = entity.properties || {};
          entity.properties.media_files = response.data.entity.properties?.media_files || [];
          setLocalMediaOrder(prev => {
            const { [mediaKey]: removed, ...rest } = prev;
            return rest;
          });
          setRefreshKey(prev => prev + 1);
        }
      } catch (error) {
        console.error("Error deleting media file:", error);
      } finally {
        setIsDeletingMedia(null);
      }
    };
    
    const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
      const files = event.target.files;
      if (!files) return;
      
      const allowedImageTypes = [
        "image/jpeg", "image/png", "image/gif", "image/webp", 
        "image/apng", "image/avif", "image/svg+xml"
      ];
      const allowedVideoTypes = [
        "video/mp4", "video/mpeg", "video/quicktime", "video/x-msvideo",
        "video/webm", "video/ogg", "video/3gpp", "video/x-flv"
      ];
      const allowedTypes = [...allowedImageTypes, ...allowedVideoTypes];
      
      const validFiles: File[] = [];
      Array.from(files).slice(0, 20).forEach((file) => {
        if (!allowedTypes.includes(file.type)) {
          console.warn(`Invalid file type: ${file.name} (${file.type})`);
          return;
        }
        
        if (file.size > 1000 * 1024 * 1024) {
          console.warn(`File too large: ${file.name}`);
          return;
        }
        
        validFiles.push(file);
      });
      
      if (validFiles.length > 0) {
        const fileList = new DataTransfer();
        validFiles.forEach(file => fileList.items.add(file));
        uploadMediaFiles(fileList.files);
      }
      
      event.target.value = "";
    };
    
    const getDisplayOrder = (mediaFile: ExistingMediaFile): number => {
      return localMediaOrder[mediaFile.key] !== undefined 
        ? localMediaOrder[mediaFile.key] 
        : (mediaFile.order || 0);
    };
    
    const getSortedMediaFiles = (): ExistingMediaFile[] => {
      const mediaFiles = entity.properties?.media_files || [];
      return [...mediaFiles].sort((a, b) => getDisplayOrder(a) - getDisplayOrder(b));
    };
    
    const getOptimalAspectRatio = (mediaFiles: ExistingMediaFile[]): string => {
      if (!mediaFiles || mediaFiles.length === 0) {
        return "aspect-[0.5625]";
      }
      
      const aspectRatios = mediaFiles
        .map(file => file.extended_metadata?.aspectRatio)
        .filter((ratio): ratio is number => typeof ratio === "number" && ratio > 0);
      
      if (aspectRatios.length === 0) {
        return "aspect-[0.5625]";
      }
      
      const hasVeryTallMedia = aspectRatios.some(ratio => ratio < 0.5625);
      if (hasVeryTallMedia) {
        return "aspect-[0.5625]";
      }
      
      const portraitRatios = aspectRatios.filter(ratio => ratio < 1 && ratio > 0.5625);
      if (portraitRatios.length > 0) {
        return "aspect-[4/5]";
      }
      
      const maxRatio = Math.max(...aspectRatios);
      return maxRatio > 0.5625 ? `aspect-[${maxRatio.toFixed(4)}]` : "aspect-[0.5625]";
    };
    
    const handleMoveMediaFile = (currentIndex: number, direction: "left" | "right") => {
      const sortedFiles = getSortedMediaFiles();
      
      let newIndex = currentIndex;
      if (direction === "left" && currentIndex > 0) {
        newIndex = currentIndex - 1;
      } else if (direction === "right" && currentIndex < sortedFiles.length - 1) {
        newIndex = currentIndex + 1;
      } else {
        return;
      }
      
      setLocalMediaOrder(prev => {
        const newOrder = { ...prev };
        
        const fileA = sortedFiles[currentIndex];
        const fileB = sortedFiles[newIndex];
        
        const orderA = getDisplayOrder(fileA);
        const orderB = getDisplayOrder(fileB);
        
        newOrder[fileA.key] = orderB;
        newOrder[fileB.key] = orderA;
        
        return newOrder;
      });
      
      setCurrentCarouselIndex(newIndex);
      
      setTimeout(() => {
        if (carouselApiRef.current) {
          carouselApiRef.current.scrollTo(newIndex);
        }
      }, 0);
    };
    
    const getMediaOrderChanges = (): { key: string; order: number }[] => {
      const changes: { key: string; order: number }[] = [];
      
      Object.entries(localMediaOrder).forEach(([key, order]) => {
        changes.push({ key, order });
      });
      
      return changes;
    };
    
    const applyMediaOrderChanges = async (): Promise<boolean> => {
      const changes = getMediaOrderChanges();
      if (changes.length === 0) return true;
      
      try {
        const response = await api.patch(apiRoutes.entities.mediaReorder(entityType, entity.id), {
          mediaOrders: changes
        });
        
        if (response.data?.entity) {
          entity.properties = entity.properties || {};
          entity.properties.media_files = response.data.entity.properties?.media_files || [];
          setLocalMediaOrder({});
          return true;
        }
        return false;
      } catch (error) {
        console.error("Error applying media order changes:", error);
        return false;
      }
    };
    
    // Coauthor handlers
    const handleCoauthorSelect = (suggestion: CoauthorSuggestion) => {
      selectCoauthor(
        suggestion,
        existingCoauthors,
        newCoauthors,
        setNewCoauthors,
        onCoauthorsChange,
        removedCoauthors
      );
    };
    
    const handleCoauthorRemove = async (coauthor: CoauthorSuggestion) => {
      await removeCoauthor(
        coauthor,
        {
          setNewCoauthors,
          setRemovedCoauthors,
          setPendingCoauthors,
          setExistingCoauthors
        },
        {
          newCoauthors,
          removedCoauthors
        },
        onCoauthorsChange
      );
    };
    
    // Privacy handler
    const handlePrivacyChange = (newPrivacy: PrivacyLevel) => {
      setPrivacy(newPrivacy);
      if (entity.properties) {
        entity.properties.privacy = newPrivacy;
      } else {
        entity.properties = { privacy: newPrivacy };
      }
      // Notify parent component
      if (onPrivacyChange) {
        onPrivacyChange(newPrivacy);
      }
    };
    
    // Render functions
    const truncateEllipsis = (node: any) => {
      return (
        <span className="read-more">
          <button 
            className="read-more-border w-full text-sm rounded-md border-b-2 justify-center items-center px-1"
            onClick={handleReadMoreClick}
          >
            <span className="flex justify-center items-center">
              <ChevronsDown className="flex justify-center items-center"/>
            </span>
          </button>
        </span>
      );
    };
    
    const renderDescription = () => {
      if (truncateDescription && !isExpanded) {
        return (
          <TruncateMarkup lines={10} tokenize="words" ellipsis={truncateEllipsis} onTruncate={(wasTruncated: any) => setIsTruncated(wasTruncated)}>
            <div>
              {transformHtmlContent(entity.body || "")}
            </div>
          </TruncateMarkup>
        );
      } else {
        return (
          <div>
            {transformHtmlContent(entity.body || "")}
            {isTruncated && (
              <span className="flex">
                <button 
                  className="read-more-border w-full text-sm rounded-md border-b-2 justify-center items-center px-1"
                  onClick={handleReadMoreClick}
                >
                  <span className="flex justify-center items-center">
                    <ChevronsUp className="flex justify-center items-center" />
                  </span>
                </button>
              </span>
            )}
          </div>
        );
      }
    };
    
    const MoreButton = React.forwardRef<
      HTMLButtonElement,
      React.ButtonHTMLAttributes<HTMLButtonElement>
    >((props, ref) => (
      <button
        type="button"
        ref={ref}
        {...props}
        className="card-more size-7 rounded-full flex justify-center items-center"
      >
        <Ellipsis className="icon-stroke size-5"/>
      </button>
    ));
    MoreButton.displayName = "MoreButton";
    
    const MoreOptions = () => {
      // User can edit/delete if they own the entity OR have the anon token
      const canEdit = (currentUser?.id && entity.user_id != null && currentUser.id.toString() === entity.user_id.toString()) || hasAnonToken;
      // Public / anonymous content authored by the viewer is eligible to
      // be promoted on ad breaks. We derive eligibility from the same
      // `entity.properties.privacy` that `EntityPrivacy` writes on edit.
      const entityPrivacy = typeof entity.properties?.privacy === "string" ? entity.properties.privacy : null;
      const isPublicOrAnonymous = entityPrivacy === "public" || entityPrivacy === "anonymous";
      const isOwner = Boolean(currentUser?.id)
        && entity.user_id != null
        && currentUser?.id?.toString() === entity.user_id.toString();
      const canPromote = isOwner && isPublicOrAnonymous;
      const handlePromoteClick = () => {
        setMenuOpen(false);
        setDrawerOpen(false);
        setIsPromoteModalOpen(true);
      };

      return (
      <>
        {!userIsMobile ? (
          <div className="flex flex-col">
            {canEdit && (
              <>
                <DropdownMenuItem onClick={handleEditClick}>
                  <div className="flex items-center gap-2 px-4 py-2 rounded-md text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
                    <Pencil className="size-4 text-eoe-icon-stroke dark:text-eoe-dark-icon-stroke"/>
                    <span>Edit</span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={handleDeleteClick}>
                  <div className="flex items-center gap-2 px-4 py-2 rounded-md text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
                    <Trash className="size-4 text-eoe-icon-stroke dark:text-eoe-dark-icon-stroke"/>
                    <span>Delete</span>
                  </div>
                </DropdownMenuItem>
              </>
            )}
            {hasAnonToken && (
              <DropdownMenuItem onClick={handleDownloadKey}>
                <div className="flex items-center gap-2 px-4 py-2 rounded-md text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
                  <Download className="size-4 text-eoe-icon-stroke dark:text-eoe-dark-icon-stroke"/>
                  <span>Download edit key</span>
                </div>
              </DropdownMenuItem>
            )}
            {canPromote && (
              <DropdownMenuItem onClick={handlePromoteClick}>
                <div className="flex items-center gap-2 px-4 py-2 rounded-md text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
                  <Megaphone className="size-4 text-eoe-icon-stroke dark:text-eoe-dark-icon-stroke"/>
                  <span>{entityCardStrings.promoteOnAdBreaks}</span>
                </div>
              </DropdownMenuItem>
            )}
            <DropdownMenuItem onClick={handleReportClick}>
              <div className="flex items-center gap-2 px-4 py-2 rounded-md text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
                <Flag className="size-4 text-eoe-icon-stroke dark:text-eoe-dark-icon-stroke"/>
                <span>Report</span>
              </div>
            </DropdownMenuItem>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {canEdit && (
              <>
                <button onClick={handleEditClick} className="flex mt-2 w-full items-center gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
                  <Pencil className="size-4"/>
                  <span>Edit</span>
                </button>
                <button onClick={handleDeleteClick} className="flex w-full items-center gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
                  <Trash className="size-4"/>
                  <span>Delete</span>
                </button>
              </>
            )}
            {hasAnonToken && (
              <button onClick={handleDownloadKey} className="flex w-full items-center gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
                <Download className="size-4"/>
                <span>Download edit key</span>
              </button>
            )}
            {canPromote && (
              <button onClick={handlePromoteClick} className="flex w-full items-center gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
                <Megaphone className="size-4"/>
                <span>{entityCardStrings.promoteOnAdBreaks}</span>
              </button>
            )}
            <button onClick={handleReportClick} className="flex w-full items-center gap-2 px-4 py-2 rounded-md hover:bg-eoe-icon-hover dark:hover:bg-eoe-dark-icon-hover text-eoe-text-sub dark:text-eoe-dark-text-sub cursor-pointer">
              <Flag className="size-4"/>
              <span>Report</span>
            </button>
          </div>
        )}
      </>
    )};
    
    const renderMediaItem = (mediaFile: ExistingMediaFile, index: number) => (
      <div key={mediaFile.key} className={`relative w-full ${getOptimalAspectRatio(getSortedMediaFiles())} flex items-center justify-center overflow-hidden bg-transparent`}>
        {mediaFile.type === "image" ? (
          <img
            src={resolveMediaUrl({ key: mediaFile.key, url: (mediaFile as { url?: string }).url, signed_url: (mediaFile as { signed_url?: string }).signed_url }, IMAGE_CDN)}
            alt={mediaFile.originalName}
            className="w-full h-full object-contain object-center"
          />
        ) : (
          <VideoPlayer
            url={resolveMediaUrl({ key: mediaFile.key, url: (mediaFile as { url?: string }).url, signed_url: (mediaFile as { signed_url?: string }).signed_url }, IMAGE_CDN)}
            muted
            controls
            objectFit="contain"
            className="w-full h-full"
            onClick={(e) => e.stopPropagation()}
          />
        )}
        
        {isDeletingMedia === mediaFile.key && (
          <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center">
            <div className="text-white text-sm">Deleting...</div>
          </div>
        )}
        
        {getSortedMediaFiles().length > 1 && (
          <div className="absolute top-2 right-2 flex items-center gap-1">
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleMoveMediaFile(index, "left");
              }}
              disabled={index === 0}
              className="p-1 bg-white bg-opacity-80 hover:bg-opacity-90 text-black rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Move left"
            >
              <ChevronLeft className="size-3" />
            </button>
            
            <div className={`rounded-full px-2 py-1 text-xs ${
              localMediaOrder[mediaFile.key] !== undefined
                ? "bg-orange-100 text-orange-800"
                : "bg-white bg-opacity-80 text-black"
            }`}>
              {index + 1}/{getSortedMediaFiles().length}
            </div>
            
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                handleMoveMediaFile(index, "right");
              }}
              disabled={index === getSortedMediaFiles().length - 1}
              className="p-1 bg-white bg-opacity-80 hover:bg-opacity-90 text-black rounded-full disabled:opacity-50 disabled:cursor-not-allowed"
              aria-label="Move right"
            >
              <ChevronRight className="size-3" />
            </button>
          </div>
        )}
        
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            deleteMediaFile(mediaFile.key);
          }}
          disabled={isDeletingMedia === mediaFile.key}
          className="absolute top-2 left-2 size-7 rounded-full bg-eoe-icon hover:bg-eoe-icon-hover text-eoe-text hover:text-black disabled:opacity-50 disabled:cursor-not-allowed"
          aria-label="Remove media"
        >
          <span className="flex items-center justify-center">
            <X className="size-3 stroke-2" />
          </span>
        </button>
      </div>
    );
    
    // `renderDisplayMediaItem` was previously used by the read-only display
    // path. That path now delegates to the shared `MediaCarousel`, which
    // owns aspect resolution, signed-URL retry, SVG fallback, and the
    // mobile media-reels tap target. Edit-mode rendering (`renderMediaItem`
    // above) keeps its own inline implementation because it owns the
    // move/remove buttons specific to the editor.

    // Reset carousel index when media files change
    useEffect(() => {
      const sortedFiles = getSortedMediaFiles();
      if (currentCarouselIndex >= sortedFiles.length && sortedFiles.length > 0) {
        setCurrentCarouselIndex(0);
      }
      //eslint-disable-next-line react-hooks/exhaustive-deps
    }, [entity.properties?.media_files, localMediaOrder, currentCarouselIndex]);
    
    return (
      <div className="entity-card card-container my-1">
        {isMediaViewerOpen ? (
          <SurfaceLayerProvider value={modalLayerProviderValue}>
            {isReportModalOpen && (
              <Report
                entityId={Number(entity.id)}
                entityType={entityType}
                open={isReportModalOpen}
                onClose={() => setIsReportModalOpen(false)}
              />
            )}
          </SurfaceLayerProvider>
        ) : (
          <>
            {isReportModalOpen && (
              <Report
                entityId={Number(entity.id)}
                entityType={entityType}
                open={isReportModalOpen}
                onClose={() => setIsReportModalOpen(false)}
              />
            )}
          </>
        )}
        <article>
          <div className={`rounded-lg flex flex-col py-2 relative ${isEntityProfilePage ? "card-main-profile-page" : "card-main"}`}>
            {showActions && (
              <div className="entity-actions-container order-last px-2 md:px-3">
                <EntityActions entity={entity as any} entityType={entityType} />
              </div>
            )}
            <div className="card-content order-first">
              {showMeta && (
                <div className="card-meta text-sm flex items-center justify-between relative px-2 md:px-3">
                  <EntityAvatar 
                    entity={entity as any}
                    entityType={entityType}
                    isEntityProfilePage={isEntityProfilePage}
                    isEntitySection={isEntitySection}
                  />
                  <div className="icon-more text-xl">
                    {userIsMobile ? (
                      <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
                        <DrawerTrigger asChild>
                          <MoreButton />
                        </DrawerTrigger>
                        <DrawerContent title="Actions">
                          <MoreOptions />
                        </DrawerContent>
                      </Drawer>
                    ) : (
                      <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen} modal={false}>
                        <DropdownMenuTrigger asChild>
                          <MoreButton />
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <MoreOptions />
                        </DropdownMenuContent>
                      </DropdownMenu>
                    )}
                  </div>
                </div>
              )}
              <div
                className="card-header mt-1 px-2 md:px-3"
                onClick={handleCardClick}
              >
                <div className="card-tags flex flex-row justify-between my-1 items-center">
                    <div className="flex flex-row flex-wrap gap-2 items-center">
                        {renderTags && renderTags(entity)}
                        <EntityPrivacy
                        privacy={privacy}
                        viewerRelation={entity.viewer_relation}
                        onPrivacyChange={() => {}}
                        actorType="user"
                        editMode={false}
                        entityId={Number(entity.id)}
                        entityType={entityType}
                        miniIcon={true}
                        showDropdown={false}
                        />
                    </div>
                    {!editMode && (
                      <div className="ignore-card-click flex-shrink-0">
                      <EntitySubscription
                        entityId={entity.id}
                        entityType={entityType}
                        variant="mini"  
                      />
                      </div>
                    )}
                </div>
                {editMode && canEditTitle ? (
                  <div className="form-control my-1">
                    <InputTextArea
                      id={`${entityType}-title`}
                      name={`${entityType}-title`}
                      labelText="Title"
                      fieldRequired={false}
                      value={entity.title || ""}
                      onChange={(e) => onTitleChange?.(e.target.value)}
                      autoComplete={`${entityType}-title`}
                      maxLength={titleMaxLength}
                      error={titleValidationMessage}
                      validationState={titleValidationMessage ? 'invalid' : (!titleValidationMessage && entity.title && entity.title.length > 0 ? 'valid' : 'none')}
                    />
                  </div>
                ) : (
                  !shouldHideTitle && (
                    <>
                      {isEntityProfilePage ? (
                        <span className="card-title line-clamp-none truncate-none">
                          {entity.title}
                        </span>
                      ) : (
                        <Link
                          to={entityHref}
                          className={`card-title ${entityType === 'teabate' ? 'line-clamp-none truncate-none' : ''}`}
                        >
                          {entity.title}
                        </Link>
                      )}
                    </>
                  )
                )}
              </div>
              <div className="flex flex-col-reverse gap-2">
                {editMode ? (
                  <>
                    <div className="flex flex-wrap w-full justify-between items-center gap-2 mt-0.5 px-2 md:px-3">
                      {canEditPrivacy && (
                        <div className="flex-shrink-0">
                          <EntityPrivacy
                            privacy={privacy}
                            onPrivacyChange={handlePrivacyChange}
                            editMode={false}
                            entityId={Number(entity.id)}
                            entityType={entityType}
                          />
                        </div>
                      )}
                      <div className="flex-shrink-0">
                        {children}
                      </div>
                    </div>
                    
                    {renderEditFields && renderEditFields(entity)}
                    
                    {/* Media Files */}
                    {(() => {
                      const sortedMediaFiles = getSortedMediaFiles();
                      const hasOrderChanges = Object.keys(localMediaOrder).length > 0;
                      
                      if (sortedMediaFiles.length === 0) return null;
                      
                      return (
                        <div className="mb-4">
                          <label className="block text-sm font-medium text-eoe-text dark:text-eoe-dark-text mb-2">
                            Media Files
                            {isUploadingMedia && (
                              <span className="ml-2 text-xs text-blue-500">Uploading...</span>
                            )}
                            {hasOrderChanges && (
                              <span className="ml-2 text-xs text-orange-500">Order changed - save to apply</span>
                            )}
                          </label>
                          <div className="relative">
                            {sortedMediaFiles.length === 1 ? (
                              renderMediaItem(sortedMediaFiles[0], 0)
                            ) : (
                              <Carousel 
                                key={`edit-carousel-${refreshKey}`}
                                className="w-full"
                                setApi={(api) => {
                                  carouselApiRef.current = api;
                                  if (api) {
                                    api.on("select", () => {
                                      setCurrentCarouselIndex(api.selectedScrollSnap());
                                    });
                                  }
                                }}
                              >
                                <CarouselContent>
                                  {sortedMediaFiles.map((mediaFile: ExistingMediaFile, index: number) => (
                                    <CarouselItem key={mediaFile.key} className="">
                                      {renderMediaItem(mediaFile, index)}
                                    </CarouselItem>
                                  ))}
                                </CarouselContent>
                                
                                <CarouselPrevious 
                                  transparent 
                                  className="absolute left-2 top-1/2 -translate-y-1/2" 
                                />
                                <CarouselNext 
                                  transparent 
                                  className="absolute right-2 top-1/2 -translate-y-1/2" 
                                />
                                
                                <CarouselDots />
                              </Carousel>
                            )}
                          </div>
                        </div>
                      );
                    })()}
                    
                    {/* Add New Media Files */}
                    <div className="mb-4">
                      <div className={`border-2 border-dashed rounded-lg p-4 ${
                        isUploadingMedia 
                          ? "border-blue-300 bg-blue-50 dark:bg-blue-900/20" 
                          : "border-eoe-border-main dark:border-eoe-dark-border-main"
                      }`}>
                        <input
                          type="file"
                          multiple
                          accept=".jpg,.jpeg,.png,.gif,.webp,.apng,.avif,.svg,.mp4,.mpeg,.mov,.avi,.webm,.ogg,.3gp,.flv"
                          onChange={handleFileSelect}
                          className="hidden"
                          id="media-upload-edit"
                          disabled={isUploadingMedia}
                        />
                        <label
                          htmlFor="media-upload-edit"
                          className={`flex items-center justify-center gap-x-2 ${
                            isUploadingMedia ? "cursor-not-allowed" : "cursor-pointer"
                          }`}
                        >
                          <ImagePlus className={`size-8 mb-2 ${
                            isUploadingMedia 
                              ? "text-blue-400 dark:text-blue-500" 
                              : "text-eoe-text-sub dark:text-eoe-dark-text-sub"
                          }`} />
                          <span className={`text-sm ${
                            isUploadingMedia 
                              ? "text-blue-600 dark:text-blue-400" 
                              : "text-eoe-text-sub dark:text-eoe-dark-text-sub"
                          }`}>
                            {isUploadingMedia 
                              ? "Uploading media files..." 
                              : "Add images and videos"
                            }
                          </span>
                        </label>
                      </div>
                    </div>
                    
                    {/* Only show description editor for non-teabates. Teabate
                        bodies are rendered as the argument list, not free
                        text, so the editor is hidden for that card kind. */}
                    {entityType !== 'teabate' && (
                      <div className="flex flex-col gap-2">
                        <TextEditor
                          value={entity.body || ""}
                          onChange={(content: string) => {
                            onDescriptionChange?.(content);
                          }}
                          editorType="entity"
                          placeholder={descriptionPlaceholder}
                          isEditorExpanded={true}
                          action="edit"
                        />
                      </div>
                    )}
                    
                    <DetailPanel 
                      title={`Collaborators (${existingCoauthors.length + pendingCoauthors.length})`} 
                      className="mb-2 text-sm bg-eoe-bg-modal dark:bg-eoe-dark-bg-modal"
                      defaultOpen={true}>
                      {isLoadingCoauthors ? (
                        <Loader2 className="size-4 animate-spin" />
                      ) : (
                        <div className="space-y-2">
                          <div className="flex flex-col gap-2">
                            <CoauthorSelect
                              inputType="add"
                              placeholder="Add collaborators"
                              className="w-full"
                              onSelect={handleCoauthorSelect}
                            />
                          </div>
                          {existingCoauthors.map((coauthor) => (
                            <EntityAvatar
                              key={`existing-${coauthor.type}-${coauthor.id}`}
                              entity={coauthor as any}
                              entityType={coauthor.type}
                              entityChip={true}
                              showCloseChip={editMode}
                              isInputField={false}
                              showLink={false}
                              onEntityChipClose={() => handleCoauthorRemove(coauthor)}
                            />
                          ))}
                          {pendingCoauthors.map((coauthor) => (
                            <EntityAvatar
                              key={`pending-${coauthor.type}-${coauthor.id}`}
                              entity={coauthor as any}
                              entityType={coauthor.type}
                              entityChip={true}
                              showCloseChip={editMode}
                              isInputField={false}
                              showLink={false}
                              badgeElement={<Clock className="size-3.5 text-yellow-500 dark:text-yellow-300" />}
                              onEntityChipClose={() => handleCoauthorRemove(coauthor)}
                            />
                          ))}
                          {newCoauthors.map((coauthor) => (
                            <EntityAvatar
                              key={`new-${coauthor.type}-${coauthor.id}`}
                              entity={coauthor as any}
                              entityType={coauthor.type}
                              entityChip={true}
                              showCloseChip={editMode}
                              isInputField={false}
                              showLink={false}
                              badgeElement={<Clock className="size-3.5 text-yellow-500 dark:text-yellow-300" />}
                              onEntityChipClose={() => handleCoauthorRemove(coauthor)}
                            />
                          ))}
                        </div>
                      )}
                    </DetailPanel>
                  </>
                ) : (
                  <>
                    {/* Only show description for non-teabates. Teabates
                        render their stance argument list in place of a
                        free-text body. */}
                    {entityType !== 'teabate' && (
                      <button
                        type="button"
                        className="card-body text-left px-2 md:px-3"
                        onClick={handleDescriptionClick}
                        aria-label="Expand entity description"
                      >
                        {renderDescription()}
                      </button>
                    )}
                    
                    <div className="flex flex-wrap gap-2">
                      {renderAdditionalContent && renderAdditionalContent(entity)}
                      
                      {(existingCoauthors.length || pendingCoauthors.length) > 0 && (
                        <DetailPanel 
                          title={`Collaborators (${existingCoauthors.length + pendingCoauthors.length})`} 
                          className="text-sm bg-eoe-bg-modal dark:bg-eoe-dark-bg-modal">
                          <Carousel className="border-none border-0">
                            <div className="flex justify-between">
                              <CarouselContent>
                                {existingCoauthors.map((coauthor) => (
                                  <CarouselItem key={`existing-${coauthor.type}-${coauthor.id}`} className="border-none basis-auto">
                                    <EntityAvatar
                                      key={`existing-${coauthor.type}-${coauthor.id}`}
                                      entity={coauthor as any}
                                      entityType={coauthor.type}
                                      entityChip={true}
                                      showCloseChip={false}
                                      isInputField={false}
                                      showLink={true}
                                    />
                                  </CarouselItem>
                                ))}
                                {pendingCoauthors.map((coauthor) => (
                                  <CarouselItem key={`pending-${coauthor.type}-${coauthor.id}`} className="border-none basis-auto">
                                    <EntityAvatar
                                      key={`pending-${coauthor.type}-${coauthor.id}`}
                                      entity={coauthor as any}
                                      entityType={coauthor.type}
                                      entityChip={true}
                                      showCloseChip={false}
                                      isInputField={false}
                                      badgeElement={<Clock className="size-3.5 text-yellow-500 dark:text-yellow-300" />}
                                      showLink={true}
                                    />
                                  </CarouselItem>
                                ))}
                              </CarouselContent>
                              <div className="flex flex-col justify-between">
                                <CarouselNext className="flex" size={"circle"}/>
                                <CarouselPrevious className="flex" size={"circle"}/>
                              </div>
                            </div>
                          </Carousel>
                        </DetailPanel>
                      )}
                    </div>
                  </>
                )}
                
                {!editMode && (
                  <div className="media-display my-1 hover:cursor-pointer">
                    {(() => {
                      if (entity.properties?.media_files && entity.properties.media_files.length > 0) {
                        const sortedMediaFiles = [...entity.properties.media_files].sort((a, b) => (a.order || 0) - (b.order || 0));

                        return (
                          <div className="media-display-container relative space-y-2 ignore-card-click">
                            <MediaCarousel
                              key={`display-carousel-${refreshKey}`}
                              mediaFiles={sortedMediaFiles}
                              imageCdn={IMAGE_CDN}
                              className="w-full"
                              showDots={sortedMediaFiles.length > 1}
                              showArrows={sortedMediaFiles.length > 1}
                              onMediaTap={() => setIsMediaViewerOpen(true)}
                            />
                          </div>
                        );
                      }

                      if (entity.url) {
                        // Allow entity-specific renderActions to override default link rendering
                        const custom = renderActions ? renderActions(entity) : null;
                        if (custom) return custom;

                        return (
                          <div className="flex flex-col gap-1">
                            <PostMediaPreview
                              url={entity.url}
                              isProfilePage={isEntityProfilePage}
                              onCardClick={handleCardClick}
                            />
                            <div className="ignore-card-click text-xs text-eoe-text-sub dark:text-eoe-dark-text-sub w-full min-w-0">
                              <PostReferenceLink url={entity.url} />
                            </div>
                          </div>
                        );
                      }

                      return renderActions ? renderActions(entity) : null;
                    })()}
                  </div>
                )}
                {!editMode && renderPrimaryContent ? (
                  // Placed after media-display in the DOM so that flex-col-reverse
                  // renders it visually above the media carousel — e.g. the graph
                  // canvas becomes the headline content for graph collections.
                  <div className="entity-card-primary-content my-1">
                    {renderPrimaryContent(entity)}
                  </div>
                ) : null}
              </div>
            </div>
          </div>
        </article>
        
        {isMediaViewerOpen ? (
          <SurfaceLayerProvider value={modalLayerProviderValue}>
            {isDeleteDialogOpen && (
              <DialogConfirmation 
                title={`Delete ${entityType.charAt(0).toUpperCase() + entityType.slice(1)}`} 
                description={`Are you sure you want to delete this ${entityType}?`} 
                isOpen={isDeleteDialogOpen} 
                onConfirm={confirmDelete} 
                onCancel={cancelDelete}
              />
            )}
            {isPromoteModalOpen && (
              <PromoteEntityModal
                isOpen={isPromoteModalOpen}
                onClose={() => setIsPromoteModalOpen(false)}
                entityType={entityType}
                entityId={Number(entity.id)}
                entityLabel={entity.title || null}
                sponsor={{
                  actorType: "user",
                  actorId: currentUser?.id ?? 0,
                }}
              />
            )}
          </SurfaceLayerProvider>
        ) : (
          <>
            {isDeleteDialogOpen && (
              <DialogConfirmation 
                title={`Delete ${entityType.charAt(0).toUpperCase() + entityType.slice(1)}`} 
                description={`Are you sure you want to delete this ${entityType}?`} 
                isOpen={isDeleteDialogOpen} 
                onConfirm={confirmDelete} 
                onCancel={cancelDelete}
              />
            )}
            {isPromoteModalOpen && (
              <PromoteEntityModal
                isOpen={isPromoteModalOpen}
                onClose={() => setIsPromoteModalOpen(false)}
                entityType={entityType}
                entityId={Number(entity.id)}
                entityLabel={entity.title || null}
                sponsor={{
                  actorType: "user",
                  actorId: currentUser?.id ?? 0,
                }}
              />
            )}
          </>
        )}

        {isMediaViewerOpen && (
          <MediaViewer
            entity={entity as any}
            entityType={entityType}
            isOpen={isMediaViewerOpen}
            onClose={() => setIsMediaViewerOpen(false)}
            renderHeader={() => (
              <div className="flex items-center gap-2 w-full">
                <EntityAvatar 
                  entity={entity as any}
                  entityType={entityType}
                  isEntityProfilePage={isEntityProfilePage}
                  isEntitySection={isEntitySection}
                />
                <div className="ml-auto flex items-center">
                  {userIsMobile ? (
                    <Drawer open={drawerOpen} onOpenChange={setDrawerOpen}>
                      <DrawerTrigger asChild>
                        <MoreButton />
                      </DrawerTrigger>
                      <DrawerContent title="Actions">
                        <MoreOptions />
                      </DrawerContent>
                    </Drawer>
                  ) : (
                    <DropdownMenu open={menuOpen} onOpenChange={setMenuOpen} modal={false}>
                      <DropdownMenuTrigger asChild>
                        <MoreButton />
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <MoreOptions />
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
            )}
          />
        )}
      </div>
    );
  }
);

EntityCard.displayName = "EntityCard";

// Named export matches how every consumer imports it (the old ui/Card
// re-export exposed it as `{ EntityCard }`); default kept for parity.
export { EntityCard };
export default EntityCard;